import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Camera, { FACING_MODES, IMAGE_TYPES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import Button from 'components/shared/Button';
import styles from './style.module.css';
import BackAndNextButton from 'layouts/RegistrationLayoutNew/Buttons/BackAndNextButton';
import BackButton from 'layouts/RegistrationLayoutNew/Buttons/BackButton';

const TakePhotoSection = ({
  handleConvertPhotoIntoBinary,
  userExist,
  uploadPhoto,
  userExistenceData,
  isMobile,
  photo,
  takePhoto,
  onNext,
  onBack,
  editDetailsOnNext,
  handleEditDetailsOnNext,
  updateOlsTracking,
  trackingId,
  emailAddress,
  title,
  referralPerson,
}) => {
  const [permission, setPermission] = useState('loading');
  const [isValidPhoto, setIsValidPhoto] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => setPermission('granted'))
      .catch(error => {
        if (error.name === 'NotAllowedError') {
          setPermission('denied');
        } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
          setPermission('occupied');
        } else {
          setPermission('denied');
        }
      });
  }, []);

  const isShutterClosed = dataUri => {
    const base64Header = 'data:image/jpeg;base64,/9j/';
    return dataUri.startsWith(base64Header) && dataUri.length < 5000;
  };

  const isValidPhotoData = dataUri => {
    return dataUri && dataUri !== 'data:,' && !isShutterClosed(dataUri);
  };

  const handleTakePhoto = dataUri => {
    if (isValidPhotoData(dataUri)) {
      setIsValidPhoto(true);
      takePhoto(dataUri);
    } else {
      setIsValidPhoto(false);
      if (isShutterClosed(dataUri)) {
        alert('Camera shutter might be closed. Please open it and try again.');
      } else if (dataUri === 'data:,') {
        alert('Camera is not ready yet. Please wait a moment...');
      }
    }
  };

  const handleNextButton = () => {
    if (!userExist) {
      if (editDetailsOnNext === 2) {
        handleEditDetailsOnNext();
      } else {
        onNext();
      }
    } else if (userExist) {
      const blob = handleConvertPhotoIntoBinary(photo);
      const formData = new FormData();
      formData.append('profilePicture', blob, 'photo.jpg');
      uploadPhoto(userExistenceData, formData);
      if (editDetailsOnNext === 2) {
        handleEditDetailsOnNext();
      } else {
        onNext();
      }
    }
    handleTrackingApi();
  };

  const handleTrackingApi = () => {
    const data = {
      session: trackingId,
      email: emailAddress,
      currentPage: title,
      data: null,
      isFormerMember: userExist,
      isReferral: Object.keys(referralPerson).length !== 0,
      referrerName: Object.keys(referralPerson).length !== 0 ? referralPerson.name : null,
      referrerCaid:
        Object.keys(referralPerson).length !== 0 ? referralPerson?.caID : null,
    };
    updateOlsTracking(data);
  };

  // Permission status
  if (permission === 'loading') {
    return <div className={styles.permissionText}>Checking camera permissions...</div>;
  }

  if (permission === 'denied') {
    return (
      <div className={styles.permissionText}>
        Camera access was denied. Please enable camera permissions and refresh the page.
      </div>
    );
  }

  if (permission === 'occupied') {
    return (
      <div className={styles.permissionText}>
        Camera is currently in use by another application. Please close other applications
        using the camera and refresh the page.
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
        {photo ? (
          <div className={styles.imgSection}>
            <img src={photo} className={styles.img} alt="photos" />
          </div>
        ) : (
          <Camera
            onTakePhoto={handleTakePhoto}
            idealFacingMode={FACING_MODES.USER}
            idealResolution={{ width: 350, height: 350 }}
            imageType={IMAGE_TYPES.JPG}
            sizeFactor={1}
          />
        )}

        <div className={`${styles.buttonsContainer} ${isMobile ? styles.mobile : ''}`}>
          <Button
            disabled={!isValidPhoto}
            className={`${styles.retakeButton} ${isMobile ? styles.mobile : ''}`}
            onClick={() => {
              takePhoto('');
              setIsValidPhoto(false);
            }}
          >
            Retake
          </Button>
          <Button
            disabled={!isValidPhoto}
            className={`${styles.nextButton} ${isMobile ? styles.mobile : ''}`}
            onClick={handleNextButton}
          >
            Next
          </Button>
        </div>

        {isMobile ? (
          <BackAndNextButton
            backButtonName="Back"
            nextButtonName="Next"
            nextButtonDisable={!isValidPhoto}
            onBack={onBack}
            onNext={handleNextButton}
          />
        ) : (
          <BackButton onBack={onBack} backButtonName="Back" />
        )}
      </div>
    </div>
  );
};

TakePhotoSection.propTypes = {
  photo: PropTypes.string.isRequired,
  takePhoto: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default TakePhotoSection;
